import { useQuery } from '@tanstack/react-query';
import { useId, useState, type ReactNode } from 'react';
import { api, ApiError } from '../api';
import './fields.css';

export type Found = { label: string; lat: number; lng: number };

/**
 * Address with a search through the server (`/api/geo/search`, OpenStreetMap). The search runs only on an explicit
 * action — "Szukaj" or Enter — never while typing (Nominatim usage policy). Picking a result sets the address and
 * the point; typing changes only the address.
 */
export function AddressSearch({
  label,
  value,
  onChange,
  onPick,
  placeholder,
  action,
}: {
  label: string;
  value: string;
  onChange: (address: string) => void;
  onPick: (found: Found) => void;
  placeholder?: string;
  /** A button next to "Szukaj" (the form: "Wybierz na mapie"). */
  action?: ReactNode;
}) {
  const id = useId();
  // the query sent ("Szukaj" / Enter) and a counter, so the same text can be searched again
  const [asked, setAsked] = useState<{ q: string; n: number } | null>(null);
  const [open, setOpen] = useState(false);
  const search = useQuery({
    queryKey: ['geo', asked?.q ?? '', asked?.n ?? 0],
    enabled: asked !== null,
    retry: false,
    staleTime: Infinity,
    queryFn: () => api<{ attribution: string; results: Found[] }>(`/api/geo/search?q=${encodeURIComponent(asked!.q)}`),
  });
  function ask() {
    const q = value.trim();
    if (q.length < 3) return;
    setAsked((a) => ({ q, n: (a?.n ?? 0) + 1 }));
    setOpen(true);
  }
  const results = open ? search.data?.results : undefined;
  const disabled = search.error instanceof ApiError && search.error.error === 'geo_disabled';
  return (
    <div className="field address-search">
      <label htmlFor={id}>{label}</label>
      <div className="address-search-box">
        <input
          id={id}
          type="search"
          value={value}
          autoComplete="off"
          placeholder={placeholder}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              ask();
            }
          }}
        />
        <button className="btn small" type="button" aria-label="Szukaj adresu" disabled={value.trim().length < 3 || search.isFetching} onClick={ask}>
          Szukaj
        </button>
        {action}
      </div>
      {open && search.isFetching && <span className="hint">Szukam adresu…</span>}
      {open && disabled && <span className="hint">Wyszukiwanie adresu wyłączone — wpisz adres ręcznie.</span>}
      {open && search.error && !disabled && <span className="field-error">{search.error instanceof ApiError ? search.error.message : 'Nie udało się wyszukać adresu'}</span>}
      {results && results.length === 0 && !search.isFetching && <span className="hint">Nie znaleziono takiego adresu. Wskaż miejsce na mapie.</span>}
      {results && results.length > 0 && (
        <ul className="address-results" aria-label="Znalezione adresy">
          {results.map((r) => (
            <li key={`${r.lat},${r.lng},${r.label}`}>
              <button
                type="button"
                onClick={() => {
                  onPick(r);
                  setOpen(false);
                }}
              >
                {r.label}
              </button>
            </li>
          ))}
          <li className="address-attribution">{search.data?.attribution ?? '© OpenStreetMap'}</li>
        </ul>
      )}
    </div>
  );
}

